if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;

        /*
         * ---------------------------------------------------------
         * VARIANT MEDIA
         * ---------------------------------------------------------
         */

        this.variantMediaIds = [];
        this.selectedVariantMediaId = '';
        this.shadeOptionPosition = 0;
        this.selectedShade = '';

        /*
         * Store the original media elements.
         * We use these references when switching variants.
         */
        this.allMediaItems = [];
        this.allThumbItems = [];
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(
          this.selectors,
          this
        );

        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider =
          this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails =
          this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom =
          this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;

        /*
         * ---------------------------------------------------------
         * GET VARIANT MEDIA IDS FROM LIQUID
         * ---------------------------------------------------------
         */

        this.variantMediaIds = (
          this.dataset.variantMediaIds || ''
        )
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);

        this.selectedVariantMediaId =
          this.dataset.selectedVariantMediaId || '';
        this.shadeOptionPosition = parseInt(
          this.dataset.shadeOptionPosition || '0',
          10
        );
        this.selectedShade =
          this.dataset.selectedShade || '';

        /*
         * ---------------------------------------------------------
         * STORE ORIGINAL MEDIA
         * ---------------------------------------------------------
         *
         * These references allow us to restore images when
         * the customer changes from one variant to another.
         */

        this.allMediaItems = [
          ...this.querySelectorAll('.product__media-item'),
        ];

        this.allThumbItems = [
          ...this.querySelectorAll('.product__thumbs-item'),
        ];

        /*
         * ---------------------------------------------------------
         * INITIAL VARIANT FILTER
         * ---------------------------------------------------------
         *
         * On page load:
         *
         * Selected variant image
         *        ↓
         * First position
         *
         * Common images
         *        ↓
         * Stay after it
         */

        if (
          (this.variantMediaIds.length ||
            this.shadeOptionPosition) &&
          (this.selectedVariantMediaId ||
            this.selectedShade)
        ) {
          this.filterVariantMedia(
            this.selectedVariantMediaId,
            false,
            null,
            this.selectedShade
          );
        }

        /*
         * ---------------------------------------------------------
         * SLIDER OPTIONS
         * ---------------------------------------------------------
         */

        this.setSliderOptions();

        const mql = window.matchMedia(
          FoxTheme.config.mediaQueryMobile
        );

        mql.onchange =
          this.updateMediaLayout.bind(this);

        this.updateMediaLayout();

        /*
         * ---------------------------------------------------------
         * IMAGE ZOOM
         * ---------------------------------------------------------
         */

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      /*
       * =========================================================
       * SLIDER OPTIONS
       * =========================================================
       */

      setSliderOptions() {
        const mediaItemGap = parseInt(
          this.dataset.mediaItemGap
        );

        const mediaItemGapMobile = parseInt(
          this.dataset.mediaItemGapMobile
        );

        this.sliderOptions = {
          init: false,

          slidesPerView: this.enableMobileThumbnails
            ? 1
            : 'auto',

          spaceBetween: mediaItemGapMobile,

          loop: false,

          grabCursor: true,

          allowTouchMove: true,

          autoHeight: true,

          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },

          navigation: {
            nextEl: this.querySelector(
              '.swiper-button-next'
            ),

            prevEl: this.querySelector(
              '.swiper-button-prev'
            ),
          },

          pagination: {
            el: this.querySelector(
              '.swiper-pagination'
            ),

            clickable: true,

            type: 'fraction',
          },

          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,

          spaceBetween: mediaItemGapMobile,

          loop: false,

          freeMode: true,

          watchSlidesProgress: true,

          threshold: 2,

          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },

            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,

              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };

            break;

          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,

              1024: {
                slidesPerView: 5,
              },

              1536: {
                slidesPerView: 7,
              },
            };

            break;
        }
      }

      /*
       * =========================================================
       * UPDATE MEDIA LAYOUT
       * =========================================================
       */

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      /*
       * =========================================================
       * INITIALIZE SLIDER
       * =========================================================
       */

      initSlider() {
        if (
          typeof this.sliderInstance !== 'object'
        ) {
          /*
           * Initialize thumbnails.
           */

          if (
            (
              this.enableDesktopSlider ||
              this.enableMobileThumbnails
            ) &&
            this.elements.thumbnails
          ) {
            this.thumbsInstance =
              new window.FoxTheme.Carousel(
                this.elements.thumbnails,
                this.thumbsOptions
              );

            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper:
                this.thumbsInstance.slider,

              autoScrollOffset: 2,
            };
          }

          /*
           * Initialize main slider.
           */

          this.sliderInstance =
            new window.FoxTheme.Carousel(
              this.elements.viewer,
              this.sliderOptions,
              [FoxTheme.Swiper.Thumbs]
            );

          this.sliderInstance.init();

          this.handleSliderAfterInit();

          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      /*
       * =========================================================
       * DESTROY SLIDER
       * =========================================================
       */

      destroySlider() {
        /*
         * Destroy main slider.
         */

        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.destroy();

          this.sliderInstance = false;
        }

        /*
         * Destroy thumbnail slider.
         */

        if (this.thumbsInstance?.slider) {
          this.thumbsInstance.slider.destroy();

          this.thumbsInstance = false;
        }
      }

      /*
       * =========================================================
       * INITIALIZE THUMB SLIDER
       * =========================================================
       */

      initThumbsSlider() {
        if (
          typeof this.thumbsInstance !== 'object'
        ) {
          this.thumbsInstance =
            new window.FoxTheme.Carousel(
              this.selectors.thumbnails,
              this.thumbsOptions
            );

          this.thumbsInstance.init();
        }
      }

      /*
       * =========================================================
       * VARIANT MEDIA FILTER
       * =========================================================
       *
       * Example:
       *
       * Product has 5 images:
       *
       * 1. White Blonde
       * 2. Vanilla Latte
       * 3. Bronzed Amber
       * 4. Model image
       * 5. Width image
       *
       * Shopify variant media:
       *
       * White Blonde  -> image 1
       * Vanilla Latte -> image 2
       * Bronzed Amber -> image 3
       *
       * Common:
       *
       * Model image  -> always show
       * Width image  -> always show
       *
       * If Vanilla Latte is selected:
       *
       * 1. Vanilla Latte
       * 2. Model
       * 3. Width
       */

      filterVariantMedia(
        selectedMediaId,
        reinitialize = true,
        variant = null,
        shade = ''
      ) {
        if (
          !this.variantMediaIds.length &&
          !this.shadeOptionPosition
        ) {
          return;
        }

        selectedMediaId = selectedMediaId
          ? String(selectedMediaId)
          : '';
        shade = shade ||
          (variant?.options?.[
            this.shadeOptionPosition - 1
          ] || '');
        shade = this.normalizeMediaToken(shade);
        this.selectedShade = shade;

        /*
         * If no selected media is available,
         * restore everything.
         */

        if (
          !selectedMediaId &&
          !shade
        ) {
          this.showAllVariantMedia();

          if (reinitialize) {
            this.reinitializeMediaSliders();
          }

          return;
        }

        /*
         * Destroy Swiper before modifying the DOM.
         */

        if (reinitialize) {
          this.destroySlider();
        }

        /*
         * Restore ALL original media first.
         *
         * This is very important when switching:
         *
         * White Blonde
         *        ↓
         * Vanilla Latte
         *        ↓
         * Bronzed Amber
         *
         * Otherwise the previous variant image could
         * remain removed from the DOM.
         */

        this.restoreAllMedia();

        const mediaList =
          this.querySelector(
            '[id^="Slider-Gallery"]'
          );

        const thumbnailList =
          this.querySelector(
            '[id^="Slider-Thumbnails"]'
          );

        /*
         * ---------------------------------------------------------
         * FILTER MAIN MEDIA
         * ---------------------------------------------------------
         */

        this.allMediaItems.forEach(
          (media) => {
            const mediaId = String(
              media.dataset.mediaId || ''
            );

            const matchesShade = this.mediaMatchesShade(
              media,
              shade
            );
            const isAssignedVariantMedia =
              this.variantMediaIds.includes(mediaId);
            const isCommonMedia =
              this.isCommonMedia(media);

            if (this.shadeOptionPosition && shade) {
              if (
                !matchesShade &&
                !isCommonMedia
              ) {
                media.remove();
              }

              return;
            }

            if (
              isAssignedVariantMedia &&
              mediaId !== selectedMediaId
            ) {
              media.remove();
            }
          }
        );

        /*
         * ---------------------------------------------------------
         * FILTER THUMBNAILS
         * ---------------------------------------------------------
         */

        this.allThumbItems.forEach(
          (thumb) => {
            const mediaId = String(
              thumb.dataset.target || ''
            );

            const matchingMedia = this.allMediaItems.find(
              (media) =>
                String(media.dataset.mediaId || '') ===
                mediaId
            );
            const matchesShade = matchingMedia
              ? this.mediaMatchesShade(matchingMedia, shade)
              : false;
            const isAssignedVariantMedia =
              this.variantMediaIds.includes(mediaId);
            const isCommonMedia = matchingMedia
              ? this.isCommonMedia(matchingMedia)
              : false;

            if (this.shadeOptionPosition && shade) {
              if (
                !matchesShade &&
                !isCommonMedia
              ) {
                thumb.remove();
              }

              return;
            }

            if (
              isAssignedVariantMedia &&
              mediaId !== selectedMediaId
            ) {
              thumb.remove();
            }
          }
        );

        /*
         * ---------------------------------------------------------
         * MOVE SELECTED VARIANT IMAGE TO FIRST
         * ---------------------------------------------------------
         *
         * This is the important part for your screenshot.
         *
         * The selected variant image will ALWAYS be
         * the first image in the gallery.
         */

        if (mediaList) {
          const selectedMedia =
            mediaList.querySelector(
              `.product__media-item[data-media-id="${selectedMediaId}"]`
            );

          if (selectedMedia) {
            mediaList.prepend(
              selectedMedia
            );
          }
        }

        /*
         * ---------------------------------------------------------
         * MOVE SELECTED VARIANT THUMBNAIL TO FIRST
         * ---------------------------------------------------------
         */

        if (thumbnailList) {
          const selectedThumbnail =
            thumbnailList.querySelector(
              `.product__thumbs-item[data-target="${selectedMediaId}"]`
            );

          if (selectedThumbnail) {
            thumbnailList.prepend(
              selectedThumbnail
            );
          }
        }

        this.reorderUnassignedMediaToEnd(
          mediaList,
          thumbnailList,
          selectedMediaId,
          shade
        );

        /*
         * Refresh DOM references after
         * modifying the gallery.
         */

        this.elements =
          window.FoxTheme.utils.queryDomNodes(
            this.selectors,
            this
          );

        /*
         * Reinitialize sliders after
         * modifying the DOM.
         */

        if (reinitialize) {
          this.reinitializeMediaSliders();
        }
      }

      normalizeMediaToken(value) {
        return String(value || '')
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      }

      mediaMatchesShade(media, shade) {
        if (!shade || !media) return false;

        const mediaText = this.normalizeMediaToken([
          media.dataset.mediaAlt,
          media.dataset.src,
        ].join(' '));

        return new RegExp(
          `(^|-)${shade}-(1|2)(-|$)`
        ).test(mediaText);
      }

      isCommonMedia(media) {
        if (!media) return true;

        const mediaText = this.normalizeMediaToken([
          media.dataset.mediaAlt,
          media.dataset.src,
        ].join(' '));

        return !/(^|-)[a-z0-9-]+-(1|2)(-|$)/.test(
          mediaText
        );
      }

      reorderUnassignedMediaToEnd(
        mediaList,
        thumbnailList,
        selectedMediaId,
        shade = ''
      ) {
        if (!mediaList && !thumbnailList) {
          return;
        }

        const rebuildItems = (
          container,
          selector,
          dataKey,
          matchFn
        ) => {
          if (!container) return;

          const items = [
            ...container.querySelectorAll(
              selector
            ),
          ];

          const selectedItem = selectedMediaId
            ? items.find(
                (item) =>
                  String(item.dataset[dataKey] || '') ===
                  String(selectedMediaId)
              )
            : null;

          const matchedShadeItems = shade
            ? items.filter((item) =>
                matchFn(item, shade)
              )
            : [];

          const orderedItems = [];

          if (selectedItem) {
            orderedItems.push(selectedItem);
          }

          matchedShadeItems.forEach((item) => {
            if (!orderedItems.includes(item)) {
              orderedItems.push(item);
            }
          });

          const remainingItems = items.filter(
            (item) => !orderedItems.includes(item)
          );

          container.innerHTML = '';

          orderedItems.forEach((item) => {
            container.appendChild(item);
          });

          remainingItems.forEach((item) => {
            container.appendChild(item);
          });
        };

        rebuildItems(
          mediaList,
          '.product__media-item',
          'mediaId',
          (item, currentShade) =>
            this.mediaMatchesShade(
              item,
              currentShade
            )
        );

        rebuildItems(
          thumbnailList,
          '.product__thumbs-item',
          'target',
          (item, currentShade) => {
            const mediaId = String(
              item.dataset.target || ''
            );

            const matchingMedia = this.allMediaItems.find(
              (media) =>
                String(media.dataset.mediaId || '') ===
                mediaId
            );

            return matchingMedia
              ? this.mediaMatchesShade(
                  matchingMedia,
                  currentShade
                )
              : false;
          }
        );
      }

      /*
       * =========================================================
       * RESTORE ALL MEDIA
       * =========================================================
       */

      restoreAllMedia() {
        const mediaList =
          this.querySelector(
            '[id^="Slider-Gallery"]'
          );

        const thumbnailList =
          this.querySelector(
            '[id^="Slider-Thumbnails"]'
          );

        /*
         * Restore original media order.
         */

        if (mediaList) {
          this.allMediaItems.forEach(
            (media) => {
              mediaList.appendChild(media);
            }
          );
        }

        /*
         * Restore original thumbnail order.
         */

        if (thumbnailList) {
          this.allThumbItems.forEach(
            (thumb) => {
              thumbnailList.appendChild(thumb);
            }
          );
        }
      }

      /*
       * =========================================================
       * SHOW ALL VARIANT MEDIA
       * =========================================================
       */

      showAllVariantMedia() {
        this.restoreAllMedia();

        this.elements =
          window.FoxTheme.utils.queryDomNodes(
            this.selectors,
            this
          );
      }

      /*
       * =========================================================
       * REINITIALIZE SLIDERS
       * =========================================================
       */

      reinitializeMediaSliders() {
        this.elements =
          window.FoxTheme.utils.queryDomNodes(
            this.selectors,
            this
          );

        /*
         * Reinitialize the correct slider
         * according to desktop/mobile layout.
         */

        this.updateMediaLayout();

        /*
         * Wait for Swiper to initialize.
         */

        requestAnimationFrame(() => {
          const selectedMedia =
            this.querySelector(
              `.product__media-item[data-media-id="${this.selectedVariantMediaId}"]`
            );

          if (!selectedMedia) {
            return;
          }

          /*
           * The selected variant image has already
           * been moved to position 0.
           *
           * Therefore always slide to index 0.
           */

          if (
            this.sliderInstance?.slider
          ) {
            this.sliderInstance.slider.slideTo(
              0,
              0,
              false
            );
          }
        });
      }

      /*
       * =========================================================
       * IMAGE ZOOM
       * =========================================================
       */

      initImageZoom() {
        let dataSource = [];

        const allMedia = [
          ...this.querySelectorAll(
            '.product__media-item:not(.swiper-slide-duplicate)'
          ),
        ];

        if (allMedia) {
          allMedia.forEach(
            (media) => {
              const {
                mediaType,
                mediaIndex,
                src,
                pswpWidth,
                pswpHeight,
              } = media.dataset;

              let source = {
                id: mediaIndex,
                mediaType: mediaType,
              };

              switch (mediaType) {
                case 'model':
                case 'video':
                case 'external_video':
                  const htmlTag =
                    mediaType === 'model'
                      ? 'product-model'
                      : 'video-element';

                  source = {
                    ...source,

                    html: `<div class="pswp__item--${mediaType}">${
                      media.querySelector(
                        htmlTag
                      ).outerHTML
                    }</div>`,
                  };

                  break;

                default:
                  /*
                   * Image.
                   */

                  source = {
                    ...source,

                    src: src,

                    width: pswpWidth,

                    height: pswpHeight,
                  };

                  break;
              }

              dataSource.push(source);
            }
          );
        }

        this.lightbox =
          new window.FoxTheme.PhotoSwipeLightbox(
            {
              dataSource: dataSource,

              pswpModule:
                window.FoxTheme.PhotoSwipe,

              bgOpacity: 1,

              arrowPrev: false,

              arrowNext: false,

              zoom: false,

              close: false,

              counter: false,

              preloader: false,

              /*
               * Don't restore focus to the opener
               * thumbnail on close.
               */

              returnFocus: false,
            }
          );

        this.lightbox.addFilter(
          'thumbEl',
          (
            thumbEl,
            { id },
            index
          ) => {
            return (
              this.querySelector(
                `.product__media-item[data-media-index="${id}"] img`
              ) || thumbEl
            );
          }
        );

        this.lightbox.addFilter(
          'placeholderSrc',
          (
            placeholderSrc,
            { data }
          ) => {
            if (data.src) {
              return data.src;
            }

            return placeholderSrc;
          }
        );

        this.lightbox.on(
          'change',
          () => {
            window.pauseAllMedia(this);

            const {
              currIndex,
            } =
              this.lightbox?.pswp;

            const slider =
              this.sliderInstance?.slider;

            if (slider) {
              slider.slideTo(
                currIndex,
                0,
                false
              );
            }
          }
        );

        this.lightbox.on(
          'destroy',
          () => {
            const {
              currIndex,
            } =
              this.lightbox?.pswp;

            if (
              !this.sliderInstance &&
              currIndex !== null
            ) {
              const target =
                this.elements.mediaItems?.find(
                  (item) =>
                    Number(
                      item.dataset.mediaIndex
                    ) === currIndex
                );

              if (target) {
                requestAnimationFrame(
                  () => {
                    const rect =
                      target.getBoundingClientRect();

                    const inView =
                      rect.top >= 0 &&
                      rect.bottom <=
                        window.innerHeight;

                    if (!inView) {
                      target.scrollIntoView(
                        {
                          behavior:
                            'smooth',

                          block:
                            'center',
                        }
                      );
                    }
                  }
                );
              }
            }
          }
        );

        this.lightbox.on(
          'pointerDown',
          (e) => {
            if (
              this.lightbox?.pswp
                ?.currSlide?.data
                ?.mediaType !== 'image'
            ) {
              e.preventDefault();
            }
          }
        );

        this.lightbox.on(
          'uiRegister',
          () => {
            const lightboxUI =
              this.lightbox?.pswp?.ui;

            if (!lightboxUI) return;

            /*
             * Previous / Next buttons.
             */

            if (!this.onlyImage) {
              lightboxUI.registerElement(
                {
                  name: 'next',

                  ariaLabel:
                    'Next slide',

                  order: 3,

                  isButton: true,

                  html: `
                    <svg
                      class="pswp-icon-next flip-x"
                      viewBox="0 0 100 100"
                    >
                      <path d="M 10,50 L 60,100 L 65,90 L 25,50 L 65,10 L 60,0 Z"></path>
                    </svg>
                  `,

                  onClick: (
                    event,
                    el
                  ) => {
                    this.lightbox?.pswp?.next();
                  },
                }
              );

              lightboxUI.registerElement(
                {
                  name: 'prev',

                  ariaLabel:
                    'Previous slide',

                  order: 1,

                  isButton: true,

                  html: `
                    <svg
                      class="pswp-icon-prev rtl-flip-x"
                      viewBox="0 0 100 100"
                    >
                      <path d="M 10,50 L 60,100 L 65,90 L 25,50 L 65,10 L 60,0 Z"></path>
                    </svg>
                  `,

                  onClick: (
                    event,
                    el
                  ) => {
                    this.lightbox?.pswp?.prev();
                  },
                }
              );
            }

            /*
             * Close button.
             */

            lightboxUI.registerElement(
              {
                name: 'close-zoom',

                ariaLabel:
                  'Close zoom image',

                order: 2,

                isButton: true,

                html: `
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    focusable="false"
                    role="presentation"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line
                      x1="18"
                      y1="6"
                      x2="6"
                      y2="18"
                    ></line>

                    <line
                      x1="6"
                      y1="6"
                      x2="18"
                      y2="18"
                    ></line>
                  </svg>
                `,

                onClick: (
                  event,
                  el
                ) => {
                  this.lightbox?.pswp?.close();
                },
              }
            );
          }
        );

        this.lightbox.init();

        /*
         * PhotoSwipe zoom click.
         */

        FoxTheme.utils.addEventDelegate(
          {
            selector:
              '.js-photoswipe--zoom',

            context: this,

            handler: (
              e,
              media
            ) => {
              if (
                media.dataset
                  ?.mediaType === 'image'
              ) {
                const index =
                  Number(
                    media.dataset
                      .mediaIndex
                  ) || 0;

                this.lightbox.loadAndOpen(
                  index
                );
              }
            },
          }
        );
      }

      /*
       * =========================================================
       * SET ACTIVE MEDIA
       * =========================================================
       *
       * This function is called when Shopify changes
       * the selected variant.
       *
       * We use variant.featured_media.id to identify
       * the image assigned to that variant.
       */

      setActiveMedia(variant) {
        if (!variant) return;

        const shade = this.normalizeMediaToken(
          variant.options?.[
            this.shadeOptionPosition - 1
          ] || ''
        );

        let featuredMedia =
          variant.featured_media;

        /*
         * Fallback for variant.featured_image.
         */

        if (
          !featuredMedia &&
          variant.featured_image
        ) {
          featuredMedia = {
            id:
              variant.featured_image.id,

            position:
              variant.featured_image
                .position || 0,

            media_type:
              'image',
          };
        }

        if (
          !featuredMedia &&
          !this.shadeOptionPosition
        ) {
          return;
        }

        /*
         * Get selected variant media ID.
         */

        const selectedMediaId = featuredMedia
          ? String(featuredMedia.id)
          : '';

        /*
         * Save selected media ID.
         */

        this.selectedVariantMediaId =
          selectedMediaId;

        /*
         * ---------------------------------------------------------
         * VARIANT PRODUCT
         * ---------------------------------------------------------
         *
         * If this image is one of the variant-specific
         * images, filter the gallery.
         */

        if (
          this.shadeOptionPosition ||
          this.variantMediaIds.includes(selectedMediaId)
        ) {
          this.filterVariantMedia(
            selectedMediaId,
            true,
            variant,
            shade
          );

          return;
        }

        /*
         * ---------------------------------------------------------
         * ORIGINAL THEME FALLBACK
         * ---------------------------------------------------------
         *
         * For products that don't use the new variant
         * media functionality, keep the original behavior.
         */

        if (
          this.sliderInstance?.slider
        ) {
          const slideIndex =
            featuredMedia.position ||
            0;

          this.sliderInstance.slider.slideTo(
            slideIndex - 1,
            0,
            false
          );
        } else {
          this.sortMediaItems(
            variant,
            featuredMedia
          );
        }
      }

      /*
       * =========================================================
       * SORT MEDIA ITEMS
       * =========================================================
       *
       * Original FoxTheme functionality.
       */

      sortMediaItems(
        variant,
        featuredMedia
      ) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (
              variant.featured_image
                ? {
                    id:
                      variant
                        .featured_image
                        .id,

                    position:
                      variant
                        .featured_image
                        .position ||
                      0,

                    media_type:
                      'image',
                  }
                : null
            );
        }

        if (!featuredMedia) return;

        let newMedias =
          this.elements.mediaItems;

        /*
         * Reset ordering.
         */

        newMedias.sort(
          function (a, b) {
            return (
              a.dataset.mediaIndex -
              b.dataset.mediaIndex
            );
          }
        );

        /*
         * Move selected media to beginning.
         */

        newMedias.some(
          (
            media,
            index
          ) => {
            if (
              media.dataset.mediaId ==
              featuredMedia.id
            ) {
              const [
                element,
              ] =
                newMedias.splice(
                  index,
                  1
                );

              newMedias.unshift(
                element
              );

              return true;
            }
          }
        );

        /*
         * Rebuild media list.
         */

        this.elements.mediaList.innerHTML =
          '';

        newMedias.forEach(
          (media) => {
            this.elements.mediaList.appendChild(
              media
            );
          }
        );

        /*
         * Desktop scroll.
         */

        if (
          !FoxTheme.config.mqlMobile &&
          this.context !== 'quickview'
        ) {
          const selectedMedia =
            this.querySelector(
              `[data-media-id="${featuredMedia.id}"]`
            );

          if (selectedMedia) {
            window.scrollTo(
              {
                top:
                  selectedMedia.offsetTop,

                behavior:
                  'smooth',
              }
            );
          }
        }
      }

      /*
       * =========================================================
       * SLIDER AFTER INIT
       * =========================================================
       */

      handleSliderAfterInit() {
        this.sliderInstance.slider.on(
          'afterInit',
          (swiper) => {
            const {
              slides,
              activeIndex,
            } = swiper;

            if (
              slides[activeIndex]
            ) {
              const isModelMediaType =
                slides[
                  activeIndex
                ].dataset
                  .mediaType ===
                'model';

              this.toggleSliderDraggableState(
                !isModelMediaType
              );
            }
          }
        );
      }

      /*
       * =========================================================
       * SLIDE CHANGE
       * =========================================================
       */

      handleSlideChange() {
        this.sliderInstance.slider.on(
          'realIndexChange',
          (swiper) => {
            const {
              slides,
              activeIndex,
              thumbs,
            } = swiper;

            /*
             * Move thumbnail slider.
             */

            if (
              thumbs.swiper
            ) {
              thumbs.swiper.slideTo(
                activeIndex
              );
            }

            /*
             * Play active media.
             */

            if (
              slides[activeIndex]
            ) {
              this.playActiveMedia(
                slides[activeIndex]
              );

              const isModelMediaType =
                slides[
                  activeIndex
                ].dataset
                  .mediaType ===
                'model';

              this.toggleSliderDraggableState(
                !isModelMediaType
              );
            }
          }
        );
      }

      /*
       * =========================================================
       * SLIDER DRAG STATE
       * =========================================================
       */

      toggleSliderDraggableState(
        isDraggable
      ) {
        if (
          this.sliderInstance
            .slider
            .allowTouchMove !==
          isDraggable
        ) {
          this.sliderInstance
            .slider
            .allowTouchMove =
            isDraggable;
        }
      }

      /*
       * =========================================================
       * PLAY ACTIVE MEDIA
       * =========================================================
       */

      playActiveMedia(
        selected
      ) {
        const deferredMedia =
          selected.querySelector(
            'product-model'
          );

        if (deferredMedia) {
          deferredMedia.loadContent(
            false
          );
        }
      }
    }
  );
}